/**
 * Footer component for University of Illinois theme.
 * Uses the official UI footer component from the brand CDN.
 * Documentation: https://cdn.brand.illinois.edu/footer.html
 */
import React, { useEffect } from 'react';
import { useSettings } from '../../contexts/SettingsContext';

export const IllinoisFooter: React.FC = () => {
  const { settings } = useSettings();
  const appName = settings?.app_name || 'dasher';

  useEffect(() => {
    // Load the official UI footer CSS
    const linkId = 'il-footer-css';
    if (!document.getElementById(linkId)) {
      const link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.href = 'https://cdn.brand.illinois.edu/components/il-footer.css';
      document.head.appendChild(link);
    }

    // Load the official UI footer JavaScript module
    const scriptId = 'il-footer-js';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'module';
      script.async = true;
      script.src = 'https://cdn.brand.illinois.edu/components/il-footer.mjs';
      document.head.appendChild(script);
    }

    // Cleanup function
    return () => {
      // Note: We don't remove the scripts/styles on unmount as they may be used by other components
      // and removing them could cause issues if the footer is re-rendered
    };
  }, []);

  return (
    <il-footer>
      <a slot="primary-unit" href="https://ncsa.illinois.edu/">
        NCSA | National Center for Supercomputing Applications
      </a>
      <a slot="site-name" href="/">
        {appName}
      </a>
      <address slot="address">
        <p>
          1205 W. Clark St.<br />
          Urbana, IL 61801
        </p>
        <p>
          Phone: <a href="tel:+12172440710">217-244-0710</a>
        </p>
        <p>
          Email: <a href="mailto:help@ncsa.illinois.edu">help@ncsa.illinois.edu</a>
        </p>
      </address>
      <nav slot="social" aria-label="Social media">
        <ul>
          <li>
            <a data-service="x" href="https://x.com/ncsa_illinois" target="_blank" rel="noopener noreferrer">
              X
            </a>
          </li>
          <li>
            <a data-service="youtube" href="https://www.youtube.com/user/NCSAatIllinois" target="_blank" rel="noopener noreferrer">
              YouTube
            </a>
          </li>
          <li>
            <a data-service="instagram" href="https://www.instagram.com/ncsa_illinois/" target="_blank" rel="noopener noreferrer">
              Instagram
            </a>
          </li>
          <li>
            <a data-service="facebook" href="https://www.facebook.com/NCSAatIllinois" target="_blank" rel="noopener noreferrer">
              Facebook
            </a>
          </li>
          <li>
            <a data-service="linkedin" href="https://www.linkedin.com/company/ncsa/" target="_blank" rel="noopener noreferrer">
              LinkedIn
            </a>
          </li>
        </ul>
      </nav>
      <div slot="actions">
        <a href="https://ncsa.illinois.edu/contact">Contact</a>
      </div>
    </il-footer>
  );
};
